import { createContext, useContext, useLayoutEffect, useState } from 'react';
import { flushSync } from 'react-dom';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof localStorage === 'undefined') return false;
    const stored = localStorage.getItem('theme');
    return stored === 'dark';
  });

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    // Keep the color-scheme meta in sync with actual state — some mobile
    // browsers' auto-dark heuristics force-invert pages whose declared
    // scheme doesn't match the live page, even when our own .dark class is off.
    document.querySelector('meta[name="color-scheme"]')
      ?.setAttribute('content', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = (e) => {
    if (!document.startViewTransition || !e) {
      setIsDark((prev) => !prev);
      return;
    }

    const x = e.clientX ?? window.innerWidth / 2;
    const y = e.clientY ?? window.innerHeight / 2;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    document.documentElement.style.setProperty('--toggle-x', `${x}px`);
    document.documentElement.style.setProperty('--toggle-y', `${y}px`);
    document.documentElement.style.setProperty('--toggle-r', `${endRadius}px`);

    document.startViewTransition(() => {
      flushSync(() => {
        setIsDark((prev) => !prev);
      });
    });
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
